import { formatCurrency, formatDateTime } from '../../../../helpers'
import { ParkingTicketType } from '../../../../services/Event/types'

/** Frase única do ABRA Park fora do ar — as duas mensagens abaixo partem dela. */
const ABRA_PARK_SEM_RESPOSTA = 'O ABRA Park não respondeu e a lista de tickets não pôde ser carregada.'

/**
 * A vigência chega como ISO 8601 com offset, mas basta um deploy do ABRA Park serializando no
 * formato do banco para o `Date` devolver Invalid Date, e a falha é silenciosa: `Invalid Date`
 * perde as duas comparações (toda comparação com NaN é falsa), então o marcador some e um ticket
 * com a vigência encerrada aparece com o rótulo limpo — o dead link que este rótulo existe para
 * evitar. As três normalizações cobrem o que o ABRA Park pode mandar:
 *
 * - `'2026-09-12'` ganha `T00:00:00`, porque um valor só-data é lido como **UTC** pela spec e em
 *   America/Sao_Paulo volta 3h, caindo no dia anterior (a deriva que o `formatApiDate` evita);
 * - `'2026-09-12 18:00:00'` troca o espaço pelo `T`, ancorado no separador data/hora;
 * - `'2026-09-12 18:00:00 -03:00'` ainda perde o espaço antes do offset — um `replace(' ', 'T')`
 *   solto trocaria só a primeira ocorrência e deixaria a string inválida do mesmo jeito.
 *
 * O que mesmo assim não der para ler vira `null`, e o rótulo sai sem marcador em vez de sair com
 * "Invalid Date" escrito nele.
 */
const parseTicketDate = (value: string | null | undefined): Date | null => {
  if (!value) return null

  const parsed = new Date(
    value
      .trim()
      .replace(/^(\d{4}-\d{2}-\d{2})$/, '$1T00:00:00')
      .replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T')
      .replace(/ (?=[+-]\d{2}:?\d{2}$)/, '')
  )

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * O nome e o preço do ticket, mais o estado quando ele não está à venda no PDV: o ABRA Park não
 * some com um ticket inativo ou fora da vigência, e nem o select do formulário — um evento
 * continua ligado ao ticket dele depois do evento.
 *
 * Fora do componente porque é pura: o `now` entra por parâmetro em vez de ser lido do relógio no
 * meio do render — o `TicketsTab` monta um só e passa o mesmo para a lista inteira.
 *
 * `is_active === false`, e não `!is_active`: uma API que ainda não mande a chave marcaria a lista
 * inteira como inativa — e ali *todos* os tickets são ativos, porque o provider filtra
 * `is_active=true` na origem.
 */
export const parkingTicketLabel = (ticket: ParkingTicketType, now: Date = new Date()): string => {
  const base = `${ticket.nome} (${ticket.preco !== null ? formatCurrency(Number(ticket.preco)) : 'Grátis'})`

  if (ticket.is_active === false) return `${base} — inativo`

  const from = parseTicketDate(ticket.disponivel_de)
  if (from !== null && from > now) return `${base} — a partir de ${formatDateTime(from.toISOString())}`

  const until = parseTicketDate(ticket.disponivel_ate)
  if (until !== null && until < now) return `${base} — encerrado em ${formatDateTime(until.toISOString())}`

  return base
}

/**
 * A mensagem do campo quando o vínculo salvo não está na lista que chegou, e `undefined` quando
 * não há o que avisar. Escrita aqui, e não no JSX, porque o mesmo texto alimenta o badge de erro
 * da aba: a condição é sintética e nunca passa pelo `fieldErrors` da API, então sem isso o Salvar
 * travava nas oito abas sem badge, tooltip ou toast que dissesse por quê.
 */
export const parkingTicketErrorMessage = (
  ticketId: number | null,
  state: { deleted: boolean; unverifiable: boolean }
): string[] | undefined => {
  if (state.deleted) {
    return [
      `O ticket #${ticketId} não existe mais no ABRA Park — foi excluído por lá. ` +
        'Escolha outro ticket ou limpe o campo para voltar ao valor manual.'
    ]
  }

  if (state.unverifiable) {
    return [`${ABRA_PARK_SEM_RESPOSTA} A associação com o ticket #${ticketId} foi mantida; salvar agora não a altera.`]
  }

  return undefined
}

/**
 * Aviso de serviço para quando não há vínculo pendente: uma lista vazia é ambígua — "não há
 * tickets atípicos" e "o ABRA Park não respondeu" são a mesma tela sem isto.
 *
 * Vai no `helperText`, não no `error`: nada do usuário está errado, e o `error` pintaria de
 * vermelho (com `aria-invalid` no select) um campo opcional e vazio *e* esconderia a explicação
 * do que ele faz — o `FormField` mostra uma coisa ou outra, nunca as duas.
 */
export const parkingTicketNotice = (abraParkAvailable: boolean, parkingTicketMissing: boolean): string | undefined =>
  !abraParkAvailable && !parkingTicketMissing
    ? `${ABRA_PARK_SEM_RESPOSTA} Você pode continuar com a receita de estacionamento preenchida manualmente.`
    : undefined
